import React, { useState, useEffect } from "react";
import {
  CircularProgress,
  Alert,
  Box,
  Typography,
} from "@mui/material"; // For loading/error

// API Functions
import { getProducts } from "../api/productApi";

// Components
import "../assets/styles/Products.css"; // Keep styles if needed
import ProductCard from "../components/ProductCard";
// Removed: import useStore from "../context/store";

function Products() {
  const [selectedSector, setSelectedSector] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [bgImage] = useState(
    "https://pa-ats.com/wp-content/uploads/sites/38/2021/12/Start-Process.jpg"
  ); // Default BG
  const [heading] = useState("Products"); // Default Heading

  useEffect(() => {
    const fetchProductsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getProducts();
        const fetchedProducts = response.data;
        setProducts(fetchedProducts);

        // Dynamically extract unique sectors
        const uniqueSectors = [
          ...new Set(fetchedProducts.map((product) => product.sector)),
        ];
        setSectors(uniqueSectors);

        // Assuming bgImage and heading might come from the first product or a separate API call
        // For now, using defaults or potentially from first product if available
        if (fetchedProducts.length > 0) {
          // Placeholder: Use a common background or fetch separately
          // setBgImage(fetchedProducts[0].someCommonBgImageField || defaultBgImage);
          // setHeading('Products'); // Or fetch a specific heading
        }
      } catch (err) {
        console.error("Fetch Products Error:", err);
        setError(err.response?.data?.message || "Failed to fetch products.");
        setProducts([]);
        setSectors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsData();
  }, []); // Fetch only once on mount

  const handleFilterClick = (sector) => {
    setSelectedSector(sector === selectedSector ? null : sector);
  };

  // Filter products based on selected sector (client-side filtering)
  const filteredProducts = selectedSector
    ? products.filter((product) => product.sector === selectedSector)
    : products;

  return (
    <div>
      {/* --- Banner Section --- */}
      <div
        style={{ backgroundImage: `url(${bgImage})` }}
        className="bg-cover bg-center background-no-repeat background-blend-multiply pt-24 md:pt-48 pb-14 md:pb-28 px-4 md:px-0"
      >
        <div className="max-w-[1400px] mx-auto flex h-full flex-col">
          <div className="products-page py-10 md:py-0">
            <h1 className="text-white md:text-8xl text-4xl text-left font-medium capitalize">
              {heading} {/* Use dynamic or default heading */}
              {selectedSector && ` - ${selectedSector}`}
            </h1>
          </div>
          {!loading &&
            sectors.length > 0 && ( // Only show filters if sectors are loaded
              <div className="filters text-left space-y-5 md:mt-24">
                <h2 className="text-white text-2xl">Category</h2>
                <div className="sector-buttons max-w-3xl flex flex-wrap gap-3">
                  {sectors.map((sector) => (
                    <button
                      key={sector}
                      onClick={() => handleFilterClick(sector)}
                      className={`px-4 py-2 capitalize ${
                        selectedSector === sector
                          ? "bg-[#f66629] border-0 text-white"
                          : "bg-[#cc673d] border-0 text-white"
                      }`}
                    >
                      {sector}
                    </button>
                  ))}
                  {/* Optional: Add a "Clear Filter" button */}
                  {selectedSector && (
                    <button
                      onClick={() => handleFilterClick(null)}
                      className="px-4 py-2 bg-red-500 border-0 text-white"
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* --- Product List Section --- */}
      <div className="product-list max-w-[1400px] mx-auto text-left px-4 md:px-0">
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ my: 4 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <>
            <h2 className="text-2xl font-medium py-10">
              {selectedSector ? `${selectedSector} products` : "All products"} (
              {filteredProducts.length})
            </h2>
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10 pb-28">
                {filteredProducts.map((product) => (
                  <ProductCard
                    // Use product._id from MongoDB as the key for stability
                    key={product._id}
                    imageUrl={product.imageUrl}
                    title={product.title}
                    // Pass description if ProductCard uses it
                    // description={product.hightLight_text} // Example
                    buttonText="More Details"
                    // Use product._id in the link
                    link={`/products/${product._id}`}
                  />
                ))}
              </div>
            ) : (
              <Typography sx={{ py: 10, textAlign: "center" }}>
                No products found{" "}
                {selectedSector ? `in the ${selectedSector} category` : ""}.
              </Typography>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Products;
